import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Shield, FileText, Check, X } from 'lucide-react';
import Header from '@/components/public/Header';
import Footer from '@/components/public/Footer';

const BRAND = '#00d68f';

const Section = ({ title, children, icon: Icon }) => (
  <div className="mb-10" data-testid={`privacy-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900 mb-3">
      {Icon && <Icon className="w-5 h-5" style={{ color: BRAND }} />} {title}
    </h2>
    <div className="text-slate-700 leading-relaxed space-y-2">{children}</div>
  </div>
);

const LinkedinPrivacyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy — StartEase AI Chrome Extension';
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="linkedin-privacy-page">
      <Header />

      <section className="bg-slate-900 py-14">
        <div className="container-custom text-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-4"
            style={{ background: `${BRAND}1a`, color: BRAND, border: `1px solid ${BRAND}55` }}
          >
            <Shield className="w-3.5 h-3.5" /> Chrome Extension
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            Privacy Policy — StartEase AI Chrome Extension
          </h1>
          <p className="text-slate-400 text-sm">Last updated: May 2026</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container-custom max-w-3xl">
          <p className="text-slate-700 leading-relaxed mb-10">
            StartEase AI is committed to protecting your privacy.
          </p>

          <Section title="What we collect" icon={Check}>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Email address</strong> — to create your account and send your verification code.</li>
              <li><strong>ICP information</strong> — what you sell, who you target, which countries — to personalise your experience.</li>
            </ul>
          </Section>

          <Section title="What we do not collect" icon={X}>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>LinkedIn profile data</strong> — page text is read locally in your browser and never stored on our servers.</li>
              <li><strong>Your LinkedIn credentials</strong> — we never ask for your LinkedIn password.</li>
              <li><strong>API keys</strong> — stored locally in Chrome only, never sent to our servers.</li>
              <li><strong>Browsing history</strong> — we do not track which profiles you visit.</li>
            </ul>
          </Section>

          <Section title="How we use your data" icon={FileText}>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Email</strong> — to send your verification code and account updates.</li>
              <li><strong>ICP information</strong> — to personalise AI outputs for your business.</li>
            </ul>
          </Section>

          <Section title="Data sharing">
            <p>We do not sell, rent, or share your personal data with any third party.</p>
          </Section>

          <Section title="Data deletion">
            <p>
              Email <a href="mailto:hi@starteaseai.com" className="font-medium" style={{ color: BRAND }}>hi@starteaseai.com</a> with
              subject <em>Delete my account</em> and we will remove all your data within 7 days.
            </p>
          </Section>

          <Section title="Contact" icon={Mail}>
            <p>
              <a href="mailto:hi@starteaseai.com" className="font-medium" style={{ color: BRAND }}>hi@starteaseai.com</a>
              <br />
              <Link to="/" className="hover:underline">starteaseai.com</Link>
            </p>
          </Section>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LinkedinPrivacyPage;
